import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check, Search, FileText } from 'lucide-react';
import { fetchNoDPP } from '../../../services/projectReportServices';

const NoDppDropdown = ({ value, onChange, accent = '#3b82f6', isReadOnly, cellInputStyle, onOpen, onClose, placeholder = 'Pilih Nomor...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Initial fetch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchNoDPP();
        const formatted = response.map(item => item.no_dpp).filter(Boolean);
        setDataList(formatted);
      } catch (error) {
        console.error("Failed to fetch No DPP:", error);
      }
      setLoading(false);
    };
    if (isOpen && dataList.length === 0) {
      fetchData();
    }
  }, [isOpen]);

  // Search logic
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (search !== undefined) { // even if empty, might want to fetch all
        setLoading(true);
        try {
          const response = await fetchNoDPP(search);
          const formatted = response.map(item => item.no_dpp).filter(Boolean);
          setDataList(formatted);
        } catch (error) {
           console.error("Failed to search No DPP:", error);
        }
        setLoading(false);
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
        setManualInput('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (isReadOnly) return;
    if (isOpen) {
      setIsOpen(false);
      setSearch('');
      setManualInput('');
      onClose?.();
    } else {
      setIsOpen(true);
      setSearch('');
      setManualInput('');
      onOpen?.();
    }
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
    setManualInput('');
    onClose?.();
  };

  const defaultStyle = {
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    padding: '10px 14px', fontSize: '13px', color: '#0f172a',
    background: '#fafafa', cursor: 'pointer', outline: 'none',
    width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
  };

  const btnStyle = { ...defaultStyle, ...cellInputStyle, cursor: isReadOnly ? 'not-allowed' : 'pointer' };

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={dropdownRef}>
      <button
        type="button"
        onClick={handleOpen}
        style={{
          ...btnStyle,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderColor: isOpen ? accent : (btnStyle.borderColor || '#e2e8f0'),
          boxShadow: isOpen ? `0 0 0 3px ${accent}20` : 'none',
          color: value ? '#0f172a' : '#94a3b8'
        }}
        disabled={isReadOnly}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: btnStyle.fontSize || '13px', fontWeight: value ? '500' : '400' }}>
            {value || placeholder}
          </span>
        </div>
        <ChevronDown size={parseInt(btnStyle.fontSize) + 1 || 14} color="#94a3b8" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            zIndex: 350, background: '#fff', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden', animation: 'dropFadeIn 0.15s ease',
            display: 'flex', flexDirection: 'column',
            maxHeight: '260px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: '10px' }} />
              <input
                autoFocus
                type="text"
                placeholder="Cari..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px 8px 32px',
                  border: '1.5px solid #e2e8f0', borderRadius: '8px',
                  fontSize: '13px', outline: 'none', color: '#0f172a',
                  fontFamily: "'Inter', sans-serif",
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
              />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '4px' }}>
            {loading ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>
                Loading...
              </div>
            ) : dataList.length > 0 ? (
              dataList.map((p, i) => (
                <div
                  key={i}
                  onClick={() => handleSelect(p)}
                  style={{
                    padding: '10px 12px', fontSize: '13px', color: '#334155',
                    cursor: 'pointer', borderRadius: '6px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: (value === p) ? `${accent}15` : 'transparent',
                    fontWeight: (value === p) ? '600' : '400',
                  }}
                  onMouseEnter={(e) => { if (value !== p) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (value !== p) e.currentTarget.style.background = 'transparent'; }}
                >
                  <FileText size={14} color={value === p ? accent : '#94a3b8'} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p}
                    </span>
                  </div>
                  {(value === p) && <Check size={14} color={accent} style={{ flexShrink: 0 }} />}
                </div>
              ))
            ) : (
              <div style={{ padding: '14px', textAlign: 'center' }}>
                <div style={{ fontSize: '12.5px', color: '#64748b', marginBottom: '10px' }}>
                  No DPP tidak ditemukan. Masukkan secara manual:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Ketik nomor manual..."
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 10px',
                      border: '1.5px solid #e2e8f0', borderRadius: '6px',
                      fontSize: '13px', outline: 'none', color: '#0f172a',
                      fontFamily: "'Inter', sans-serif",
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = accent}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <button
                    onClick={() => handleSelect(manualInput)}
                    disabled={!manualInput.trim()}
                    style={{
                      width: '100%', padding: '8px 10px',
                      background: manualInput.trim() ? accent : '#e2e8f0',
                      color: manualInput.trim() ? '#fff' : '#94a3b8',
                      border: 'none', borderRadius: '6px',
                      fontSize: '13px', fontWeight: '600',
                      cursor: manualInput.trim() ? 'pointer' : 'not-allowed',
                      transition: 'background 0.2s'
                    }}
                  >
                    Gunakan Nomor Ini
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NoDppDropdown;
